<template>
  <div>
    <BaseListPage
        id="orderListTable"
        ref="listPage"
        :function-key="functionKey"
        :name="name"
        :base-query="baseQuery"
        :store-path="storePath"
        :response-path="responsePath"
        :save-key="saveKey"
        :table-set-list="_tableSetList"
        :table-data-list="sortedList"
        :show-selection="showSelection"
        :no-search="noSearch"
        :row-key="rowKey"
        :min-column-width="'200px'"
        :is-max-get-count="isMaxGetCount"
        :async-render="true"
        :cell-style="cellStyle"
        title="注文一覧"
        title-class="order-header"
        icon="order"
        :table-class="tableClass"
        @dataConvert="dataConvert"
        @row-click="onRowClick">
      <span
          v-show="mode === ACTION_MODE.NORMAL"
          slot="headerAction"
          class="action_button">
        <el-dropdown @command="actionCommand">
          <el-button size="small">
            アクション
            <i class="el-icon-arrow-down el-icon--right" />
          </el-button>
          <el-dropdown-menu
              slot="dropdown"
              class="header-action-menu">
            <el-dropdown-item
                v-if="$isFunctionShown(define.PermissionKeys.Order.List.MAIL)"
                :disabled="disableMailButton"
                :command="ACTION_MODE.MAIL">
              メール
            </el-dropdown-item>
            <el-dropdown-item :command="ACTION_MODE.SEARCH">
              検索条件設定
            </el-dropdown-item>
            <el-dropdown-item :command="ACTION_MODE.DISPLAY">
              表示項目設定
            </el-dropdown-item>
            <ToDownload
                page-key="order_list"
                :conditions="conditions"
                :keyword="searchKeyword"
                :is-and-search="isAndSearch" />
          </el-dropdown-menu>
        </el-dropdown>
      </span>
      <span
          v-show="mode !== ACTION_MODE.NORMAL"
          slot="headerActionMode"
          class="action_button">
        <el-button size="small"
            @click="cancel">
          キャンセル
        </el-button>
        <el-button
            v-show="mode === ACTION_MODE.MAIL"
            :disabled="selectQty === 0"
            size="small"
            @click="sendMail">
          送信
        </el-button>
      </span>
      <template
          slot="tableOperation"
          slot-scope="{ row }">
        <BaseOperationTooltip
            v-if="showTooltip"
            :list="getTooltipList(row.row)"
            class="operation-button"
            placement="bottom-end" />
      </template>
      <template
          slot="prefixTable">
        <div class="prefixTableRow">
          <el-button size="small"
              @click="clickDisplaySelect">
            表示項目
          </el-button>
          <el-button size="small"
              @click="clickDisplaySave">
            表示項目保存
          </el-button>
        </div>
        <div v-show="mode === ACTION_MODE.MAIL"
            class="prefixTableRow">
          <span>
            {{
              `メール種類： ${((mailTemplate || []).find((template) => template.uuid === selectedMailTemplate) || {}).templateName || ''}`
            }}
          </span>
          <span>
            {{
              `対象注文件数： ${selectQty} 件`
            }}
          </span>
        </div>
      </template>
    </BaseListPage>
    <BaseDialog
        title="メールテンプレート選択"
        width="35%"
        class="mailTemplateDialog"
        :show-only-close-button="false"
        :visible.sync="mailTemplateDialogVisible"
        @closed="mailTemplateDialogCancelClose"
        @cancel="mailTemplateDialogCancelClose"
        @ok="mailTemplateDialogOK">
      <template slot="contents">
        <div class="mailTemplateContents">
          <span>
            テンプレート名
          </span>
          <el-select
              v-model="selectedMailTemplate"
              v-validate="'required'"
              data-vv-scope="template"
              name="template"
              data-vv-name="テンプレート">
            <el-option
                v-for="option in mailTemplate"
                :key="option.uuid"
                :label="option.templateName"
                :value="option.uuid" />
          </el-select>
          <span v-if="errors.has('template')">
            {{ errors.first('template') }}
          </span>
        </div>
      </template>
    </BaseDialog>

    <!-- O-Pluxイベント更新 -->
    <OpluxDialog
        :oplux-data="opluxData"
        :visible.sync="isOpluxDialog" />

    <!-- 検索条件設定 -->
    <BaseSearchConditionListModal
        function-key="order_list"
        :store-path="storePath"
        :visible.sync="isSearchConditionListDialog" />

    <!-- 表示項目設定 -->
    <BaseDisplayListModal
        function-key="order_list"
        :display-config="displayItems"
        :store-path="storePath"
        :visible.sync="isDisplayListDialog" />
    <!-- 表示項目 -->
    <BaseDisplaySelectModal
        function-key="order_list"
        :table-set-list="_tableSetList"
        :display-config="displayItems"
        :store-path="storePath"
        :visible.sync="isDisplaySelectDialog" />
    <!-- 表示項目保存 -->
    <BaseDisplaySaveModal
        function-key="order_list"
        :display-config="displayItems"
        :visible.sync="isDisplaySaveDialog" />
  </div>
</template>

<script>
import _ from 'lodash'
import { mapState, mapGetters } from 'vuex'
import moment from 'moment'

import * as config from '@/config'
import * as define from '@/define'
import * as query from '@/queries/order'
import * as mailQuery from '@/queries/mail'
import common from '@/utils/common'
import message from '@/utils/message'

import x from '@/components/mixins/mixin'
import orderUtil from '@/components/mixins/orderUtil'

import { ELEMENT_TYPE } from '@/components/molecules/BaseCell'
import BaseListPage from '@/components/organisms/BaseListPage'
import BaseDialog from '@/components/molecules/BaseDialog'
import BaseOperationTooltip from '@/components/molecules/BaseOperationTooltip'
import confirmDiscardOnLeave from '@/components/mixins/confirmDiscardOnLeave'

import BaseDisplayListModal from '@/components/organisms/BaseDisplayListModal'
import BaseDisplaySaveModal from '@/components/organisms/BaseDisplaySaveModal'
import BaseDisplaySelectModal from '@/components/organisms/BaseDisplaySelectModal'

import BaseSearchConditionListModal from '@/components/organisms/BaseSearchConditionListModal'
import ToDownload from '@/components/organisms/ToDownload'
import OpluxDialog from '@/components/pages/order/OpluxDialog'

/**
 * 注文一覧画面 アクションモード
 */
export const ACTION_MODE = Object.freeze(
  {
    NORMAL: 0,         // 通常
    MAIL: 1,           // メール送信
    SEARCH: 2,         // 検索条件
    DISPLAY: 3         // 表示項目
  }
)

export const PRODUCT_TYPE = Object.freeze(
  [
    'フィジカルのみ',
    'デジタルのみ',
    '混在'
  ]
)

export default {
  name: 'OrderList',
  components: {
    BaseListPage,
    BaseDialog,
    BaseOperationTooltip,
    BaseDisplayListModal,
    BaseDisplaySaveModal,
    BaseDisplaySelectModal,
    BaseSearchConditionListModal,
    ToDownload,
    OpluxDialog
  },
  mixins: [ x, orderUtil, confirmDiscardOnLeave ],
  provide () {
    return {
      parentValidator: this.$validator
    }
  },
  data () {
    return {
      showSelection: false,
      mode: ACTION_MODE.NORMAL,
      mailTemplate: [],
      mailTemplateDialogVisible: false,
      selectedMailTemplate: '',
      isOpluxDialog: false,
      opluxData: {},
      isDisplayListDialog: false,
      isDisplaySelectDialog: false,
      isDisplaySaveDialog: false,
      isSearchConditionListDialog: false
    }
  },
  computed: {
    define: () => define,
    ACTION_MODE: () => ACTION_MODE,
    ...mapState({
      tableSetList: (state) => state.Order.tableSetList || false,
      searchKeyword: (state) => state.Order.searchKeyword || '',
      tdLists: (state) => state.Order.tdLists || [],
      isAndSearch: (state) => state.Order.isAndSearch || false,
      conditions: (state) => state.Order.searchConditions || {},
      timeZone: (state) => state.timeZone || 'Asia/Tokyo',
      displayItems: (state) => state.Order.displayItems
    }),
    ...mapGetters({
      getSortedList: 'sortedList',
      searchConditions: 'searchConditions',
      getServiceSiteConfig: 'getServiceSiteConfig',
      customCellStyle: 'customCellStyle',
      sites: 'sites'
    }),
    ...mapGetters('LocalStorage', {
      sortCondition: 'sortCondition'
    }),
    functionKey () {
      return 'order_list'
    },
    name () {
      return 'orderList'
    },
    storePath () {
      return 'Order'
    },
    saveKey () {
      return 'orderCondition'
    },
    responsePath () {
      return 'data.latestOrders.edges'
    },
    linkKey () {
      return ['node.site.code', 'node.orderNo']
    },
    returnPath () {
      return '/order'
    },
    baseQuery () {
      return query.GRAPH_QL_QUERY_ORDER_LIST
    },
    sortedList () {
      return this.getSortedList(this.storePath, this.sortCondition(this.name))
    },
    noSearch () {
      return this.mode !== ACTION_MODE.NORMAL
    },
    rowKey () {
      return 'node.id'
    },
    selectQty () {
      return this.mode === ACTION_MODE.NORMAL ? 0
        : _.get(this.$refs, 'listPage.$refs.tableList.$refs.table.selection.length', 0)
    },
    tableClass () {
      return `orderlist-table-list ${this.mode === ACTION_MODE.NORMAL ? 'row-clickable-table' : ''}`
    },
    _tableSetList () {
      // 注文一覧の左から順に記載する。(display_orderは表示設定の画面でしか効かない)
      const returnValue = [
        { // 保留数
          itemKey: 'pendingCount', label: '保留数',
          isAvailable: false, isActive: false, sortable: 'custom', type: 'String',
          path: 'node.pendingCount',
          funcId: define.PermissionKeys.Order.List.PENDING,
          width: '100px'
        },
        { // サイト
          itemKey: 'site', isAvailable: false, isActive: false,
          type: 'String', label: 'サイト', sortable: 'custom',
          path: ['node.site.code', 'node.site.name'], colsep: ': ',
          name: 'node.site.code', editable: true,
          funcId: define.PermissionKeys.Order.List.SITE
        },
        { // 注文番号
          itemKey: 'orderNo', isAvailable: false, isActive: false,
          type: 'String', label: '注文番号', sortable: 'custom',
          path: 'node.orderNo',
          funcId: define.PermissionKeys.Order.List.ORDER_NUMBER,
          width: '160px'
        },
        { // 配送番号
          itemKey: 'shipmentId', isAvailable: false, isActive: false,
          type: 'String', label: '配送番号', sortable: true,
          elementType: ELEMENT_TYPE.HTML_FUNCTION,
          path: (row) => this.getDisplayData(row, 'node.shipments', ['shipmentId'], '', 'String'),
          funcId: define.PermissionKeys.Order.List.SHIPMENT_ID, class: 'innerItem'
        },
        { // 伝票番号
          itemKey: 'slipId', isAvailable: false, isActive: false,
          type: 'String', label: '伝票番号', sortable: true,
          elementType: ELEMENT_TYPE.HTML_FUNCTION,
          path: (row) => this.getDisplayData(row, 'node.shippeds', ['trackingNumber'], '', 'String'),
          sortBy: (row) => this.getSortableData(row, 'node.shippeds', ['trackingNumber'], '', 'String'),
          funcId: define.PermissionKeys.Order.List.TRACKING_NO, class: 'innerItem'
        },
        { // 注文日時
          itemKey: 'orderDatetime', isAvailable: false, isActive: false,
          type: 'Date', label: '注文日時', sortable: 'custom',
          path: 'node.orderDatetime',
          funcId: define.PermissionKeys.Order.List.ORDER_DATETIME
        },
        { // 出荷日時
          itemKey: 'slipDatetime', isAvailable: false, isActive: false,
          type: 'Date', label: '出荷日時', sortable: 'custom',
          elementType: ELEMENT_TYPE.HTML_FUNCTION,
          path: (row) => this.getDisplayData(row, 'node.shippeds', ['slipDatetime'], '', 'Datetime'),
          sortBy: (row) => this.getSortableData(row, 'node.shippeds', ['slipDatetime'], '', 'Datetime'),
          funcId: define.PermissionKeys.Order.List.ORDER_DATETIME, class: 'innerItem',
          width: '250px'
        },
        { // 指図日時
          itemKey: 'exportedDatetime', isAvailable: false, isActive: false,
          type: 'Date', label: '指図日時', sortable: 'custom',
          elementType: ELEMENT_TYPE.HTML_FUNCTION,
          path: (row) => this.getDisplayData(row, 'node.exports', ['exportedAt'], '', 'Datetime'),
          sortBy: (row) => this.getSortableData(row, 'node.exports', ['exportedAt'], '', 'Datetime'),
          funcId: define.PermissionKeys.Order.List.ORDER_DATETIME, class: 'innerItem',
          width: '250px'
        },
        { // 配達完了日
          itemKey: 'deliveryDatetime', isAvailable: false, isActive: false,
          type: 'Date', label: '配達完了日', sortable: 'custom',
          elementType: ELEMENT_TYPE.HTML_FUNCTION,
          path: (row) => this.getDisplayData(row, 'node.arriveds', ['deliveryDate'], '', 'Datetime'),
          sortBy: (row) => this.getSortableData(row, 'node.arriveds', ['deliveryDate'], '', 'Datetime'),
          funcId: define.PermissionKeys.Order.List.ORDER_DATETIME, class: 'innerItem',
          width: '250px'
        },
        { // 注文ステータス(標準)
          itemKey: 'orderStatus', isAvailable: false, isActive: false,
          type: 'String', label: '注文ステータス', elementType: ELEMENT_TYPE.TAG,
          path: this.orderStatus,
          class: 'left order-status-tag', size: 'small', width: '250px',
          funcId: define.PermissionKeys.Order.List.ORDER_STATUS
        },
        { // 支払ステータス
          itemKey: 'paymentStatus', isAvailable: false, isActive: false,
          type: 'String', label: '支払ステータス', sortable: 'custom',
          // path: 'node.isPaid', codeName: 'd_order.is_paid',
          path: this.paymentStatus, elementType: ELEMENT_TYPE.FUNCTION,
          class: 'left', funcId: define.PermissionKeys.Order.List.IS_PAID
        },
        { // 支払方法
          itemKey: 'paymentMethod', isAvailable: false, isActive: false,
          type: 'String', label: '支払方法', sortable: 'custom',
          class: 'left payment-method-tag', size: 'small',
          elementType: ELEMENT_TYPE.TAG,
          path: this.paymentMethod,
          sortBy: (row) => { return row.node.payments[0].paymentMethod.code },
          funcId: define.PermissionKeys.Order.List.PAYMENT_METHOD,
          width: '270px'
        },
        { // 購入金額
          itemKey: 'totalGrossPrice', isAvailable: false, isActive: false,
          type: 'String', label: '購入金額', sortable: true,
          class: 'right',
          elementType: ELEMENT_TYPE.FUNCTION,
          sortBy: (row) => { return Number(row.node.orderTotalPrice.gross) },
          path: this.grossPrice,
          funcId: define.PermissionKeys.Order.List.PURCHASE_AMOUNT
        },
        { // 商品金額
          itemKey: 'productGrossPrice', isAvailable: false, isActive: false,
          type: 'String', label: '商品金額', sortable: true,
          class: 'innerItem',
          elementType: ELEMENT_TYPE.HTML_FUNCTION,
          path: (row) => this.getDisplayPrice(row, 'node.shipments', 'product'),
          sortBy: (row) => { return this.getSortablePrice(row, 'node.shipments', 'product') },
          funcId: define.PermissionKeys.Order.List.PRODUCT_AMOUNT
        },
        { // 送料
          itemKey: 'shippingGrossPrice', isAvailable: false, isActive: false,
          type: 'String', label: '送料', sortable: true,
          class: 'innerItem',
          elementType: ELEMENT_TYPE.HTML_FUNCTION,
          path: (row) => this.getDisplayPrice(row, 'node.shipments', 'shipping'),
          sortBy: (row) => { return this.getSortablePrice(row, 'node.shipments', 'shipping') },
          funcId: define.PermissionKeys.Order.List.SHIPPING_AMOUNT
        },
        { // 決済手数料
          itemKey: 'feeGrossPrice', isAvailable: false, isActive: false,
          type: 'String', label: '決済手数料', sortable: true,
          class: 'right',
          elementType: ELEMENT_TYPE.HTML_FUNCTION,
          path: (row) => this.getGrossPrice(row, 'fee'),
          sortBy: (row) => { return this.getGrossPriceValue(row, 'fee') },
          funcId: define.PermissionKeys.Order.List.FEE_AMOUNT
        },
        { // その他
          itemKey: 'otherGrossPrice', isAvailable: false, isActive: false,
          type: 'String', label: 'その他', sortable: true,
          class: 'right',
          elementType: ELEMENT_TYPE.FUNCTION,
          path: (row) => this.getMerchandiseAdjustGrossPrice(row),
          sortBy: (row) => { return this.getMerchandiseAdjustGrossPriceValue(row) },
          funcId: define.PermissionKeys.Order.List.OTHER_AMOUNT
        },
        { // エール
          itemKey: 'yellGrossPrice', isAvailable: false, isActive: false,
          type: 'String', label: 'エール', sortable: true,
          class: 'right',
          elementType: ELEMENT_TYPE.FUNCTION,
          path: (row) => this.getAdjustGrossPrice(row),
          sortBy: (row) => { return this.getAdjustGrossPriceValue(row) },
          funcId: define.PermissionKeys.Order.List.YELL_AMOUNT
        },
        { // 顧客番号
          itemKey: 'customerNo', isAvailable: false, isActive: false,
          type: 'String', label: '顧客番号', sortable: 'custom', path: 'node.customerNo',
          funcId: define.PermissionKeys.Order.List.ORDERER_NAME,
          width: '100px'
        },
        { // 注文者氏名
          itemKey: 'customerName', isAvailable: false, isActive: false,
          type: 'String', label: '注文者氏名', sortable: true,
          sortBy: (row) => { return row.node.orderer.lastName + ' ' + row.node.orderer.firstName },
          path: ['node.orderer.lastName', 'node.orderer.firstName'], colsep: ' ',
          funcId: define.PermissionKeys.Order.List.ORDERER_NAME
        },
        { // 注文者氏名カナ
          itemKey: 'customerKana', isAvailable: false, isActive: false,
          type: 'String', label: '注文者氏名（カナ）', sortable: true,
          sortBy: (row) => { return row.node.orderer.lastNameKana + ' ' + row.node.orderer.firstNameKana },
          path: ['node.orderer.lastNameKana', 'node.orderer.firstNameKana'], colsep: ' ',
          funcId: define.PermissionKeys.Order.List.ORDERER_NAME_KANA
        },
        { // 注文者メールアドレス
          itemKey: 'customerEmail', isAvailable: false, isActive: false,
          type: 'String', label: '注文者メールアドレス', sortable: true,
          sortBy: (row) => { return row.node.customerEmail },
          path: 'node.customerEmail',
          funcId: define.PermissionKeys.Order.List.ORDERER_MAIL,
          width: '250px'
        },
        { // 注文者TEL
          itemKey: 'customerPhone', isAvailable: false, isActive: false,
          type: 'String', label: '注文者TEL', sortable: false,
          path: 'node.orderer.phone',
          funcId: define.PermissionKeys.Order.List.ORDERER_PHONE,
          width: '140px'
        },
        { // 注文者〒
          itemKey: 'customerPostalCode', isAvailable: false, isActive: false,
          type: 'String', label: '注文者〒', sortable: true,
          sortBy: (row) => { return Number(row.node.address.postalCode) },
          path: 'node.address.postalCode',
          funcId: define.PermissionKeys.Order.List.ORDERER_POSTAL_CODE,
          width: '100px'
        },
        { // 注文者住所(都道府県)
          itemKey: 'customerStateCode', isAvailable: false, isActive: false,
          type: 'String', label: '注文者住所(都道府県)', sortable: false,
          sortBy: (row) => {
            return row.node.address.stateCode + ' ' + row.node.address.city + ' ' +
              row.node.address.address1 + ' ' + row.node.address.address2 + ' ' +
              row.node.address.suite
          },
          path: 'node.address.stateCode',
          funcId: define.PermissionKeys.Order.List.ORDERER_STATE,
          width: '100px'
        },
        { // 注文者住所(市区町村)
          itemKey: 'customerCity', isAvailable: false, isActive: false,
          type: 'String', label: '注文者住所(市区町村)', sortable: false,
          path: 'node.address.city',
          funcId: define.PermissionKeys.Order.List.ORDERER_CITY,
          width: '140px'
        },
        { // 注文者住所(番地)
          itemKey: 'customerAddress', isAvailable: false, isActive: false,
          type: 'String', label: '注文者住所(番地)', sortable: false,
          path: 'node.address.address1',
          funcId: define.PermissionKeys.Order.List.ORDERER_ADDRESS_1,
          width: '250px'
        },
        { // 注文者住所(建物名・部屋番号)
          itemKey: 'customerSuite', isAvailable: false, isActive: false,
          type: 'String', label: '注文者住所(建物名・部屋番号)', sortable: false,
          path: 'node.address.suite',
          funcId: define.PermissionKeys.Order.List.ORDERER_SUITE,
          width: '250px'
        },
        { // 配送先氏名
          itemKey: 'shipmentName', isAvailable: false, isActive: false,
          type: 'String', label: '配送先氏名', sortable: true,
          path: (row) => this.getDisplayData(row, 'node.shipments', ['receiver.lastName', 'receiver.firstName'], ' ', 'String'),
          sortBy: (row) => this.getSortableData(row, 'node.shipments', ['receiver.lastName', 'receiver.firstName'], ' ', 'String'),
          elementType: ELEMENT_TYPE.HTML_FUNCTION,
          funcId: define.PermissionKeys.Order.List.SHIPMENT_NAME, class: 'innerItem'
        },
        { // 配送先氏名カナ
          itemKey: 'shipmentKana', isAvailable: false, isActive: false,
          type: 'String', label: '配送先氏名（カナ）', sortable: true,
          path: (row) => this.getDisplayData(row, 'node.shipments', ['receiver.lastNameKana', 'receiver.firstNameKana'], ' ', 'String'),
          sortBy: (row) => this.getSortableData(row, 'node.shipments', ['receiver.lastNameKana', 'receiver.firstNameKana'], ' ', 'String'),
          elementType: ELEMENT_TYPE.HTML_FUNCTION,
          funcId: define.PermissionKeys.Order.List.SHIPMENT_NAME_KANA, class: 'innerItem'
        },
        { // 配送先TEL
          itemKey: 'shipmentPhone', isAvailable: false, isActive: false,
          type: 'String', label: '配送先TEL', sortable: false,
          path: (row) => this.getDisplayData(row, 'node.shipments', ['receiver.phone'], '', 'String'),
          elementType: ELEMENT_TYPE.HTML_FUNCTION,
          funcId: define.PermissionKeys.Order.List.SHIPMENT_PHONE, class: 'innerItem'
        },
        { // 配送先〒
          itemKey: 'shipmentPostalCode', isAvailable: false, isActive: false,
          type: 'String', label: '配送先〒', sortable: true,
          path: (row) => this.getDisplayData(row, 'node.shipments', ['address.postalCode'], '', 'String'),
          sortBy: (row) => this.getSortableData(row, 'node.shipments', ['address.postalCode'], '', 'String'),
          elementType: ELEMENT_TYPE.HTML_FUNCTION,
          funcId: define.PermissionKeys.Order.List.SHIPMENT_POSTAL_CODE, class: 'innerItem',
          width: '100px'
        },
        { // 配送先住所(都道府県)
          itemKey: 'shipmentStateCode', isAvailable: false, isActive: false,
          type: 'String', label: '配送先住所(都道府県)', sortable: false,
          path: (row) => this.getDisplayData(row, 'node.shipments', ['address.stateCode'], '', 'String'),
          sortBy: (row) => this.getSortableData(row, 'node.shipments', ['address.stateCode'], '', 'String'),
          elementType: ELEMENT_TYPE.HTML_FUNCTION,
          funcId: define.PermissionKeys.Order.List.SHIPMENT_STATE, class: 'innerItem',
          width: '100px'
        },
        { // 配送先住所(市区町村)
          itemKey: 'shipmentCity', isAvailable: false, isActive: false,
          type: 'String', label: '配送先住所(市区町村)', sortable: false,
          path: (row) => this.getDisplayData(row, 'node.shipments', ['address.city'], '', 'String'),
          sortBy: (row) => this.getSortableData(row, 'node.shipments', ['address.city'], '', 'String'),
          elementType: ELEMENT_TYPE.HTML_FUNCTION,
          funcId: define.PermissionKeys.Order.List.SHIPMENT_CITY, class: 'innerItem'
        },
        { // 配送先住所(番地)
          itemKey: 'shipmentAddress', isAvailable: false, isActive: false,
          type: 'String', label: '配送先住所(番地)', sortable: false,
          path: (row) => this.getDisplayData(row, 'node.shipments', ['address.address1'], '', 'String'),
          sortBy: (row) => this.getSortableData(row, 'node.shipments', ['address.address1'], '', 'String'),
          elementType: ELEMENT_TYPE.HTML_FUNCTION,
          funcId: define.PermissionKeys.Order.List.SHIPMENT_ADDRESS_1, class: 'innerItem'
        },
        { // 配送先住所(建物名・部屋番号)
          itemKey: 'shipmentSuite', isAvailable: false, isActive: false,
          type: 'String', label: '配送先住所(建物名・部屋番号)', sortable: false,
          path: (row) => this.getDisplayData(row, 'node.shipments', ['address.suite'], '', 'String'),
          sortBy: (row) => this.getSortableData(row, 'node.shipments', ['address.suite'], '', 'String'),
          elementType: ELEMENT_TYPE.HTML_FUNCTION,
          funcId: define.PermissionKeys.Order.List.SHIPMENT_SUITE, class: 'innerItem'
        },
        { // 配送先メールアドレス
          itemKey: 'shipmentEmail', isAvailable: false, isActive: false,
          type: 'String', label: '配送先メールアドレス', sortable: true,
          path: (row) => this.getDisplayData(row, 'node.shipments', ['receiver.email'], '', 'String'),
          sortBy: (row) => this.getSortableData(row, 'node.shipments', ['receiver.email'], '', 'String'),
          elementType: ELEMENT_TYPE.HTML_FUNCTION,
          funcId: define.PermissionKeys.Order.List.SHIPMENT_MAIL, class: 'innerItem',
          width: '250px'
        }
      ]

      // 属性情報の追加
      for (const displayItem of (this.displayItems || []).filter((data) => data.isAttribute)) {
        returnValue.push(
          {
            itemKey: displayItem.itemKey, elementType: displayItem.attributeProperty.indexOf('.') === -1 ? ELEMENT_TYPE.FUNCTION : ELEMENT_TYPE.HTML_FUNCTION,
            type: 'String', label: displayItem.attributeDisplayName, class: displayItem.attributeProperty.indexOf('.') === -1 ? '' : 'innerItem',
            path: (row) => {
              // attributePropertyにドットがない場合、階層直下を直接参照
              if (displayItem.attributeProperty.indexOf('.') === -1) {
                const attribute = _.get(row, `node.attributes`, [])
                const returnValue = attribute.find((data) => data.attrId === displayItem.attributeProperty)
                if (returnValue != null) {
                  if (returnValue.attrId === 'orderProductCategory') {
                    return PRODUCT_TYPE[returnValue.attrValue] || ''
                  }
                  return (returnValue || {}).attrValue || ''
                }
                return ''
              } else { // attributePropertyにドットが含まれる場合、対応するattributesを参照
                const pathArray = displayItem.attributeProperty.split('.')

                // ドット区切りのパスからattributes検索用のattributePropertyを取り出し
                // 残りを目的のattributesまでのパスとする
                // (ex. a.b.estimatedDeliveryDateText → pathArray:[0]=a [1]=b, attributeProperty:estimatedDeliveryDateText→path)
                const attributeProperty = pathArray.pop()
                let htmlStr = ` <table class='innerTable'> `
                const nodes = _.get(row, `node`, [])
                htmlStr += this.digAttributeValueForHtml(nodes, pathArray, 0, attributeProperty, htmlStr)
                htmlStr += ' </table> '
                return htmlStr
              }
            },
            sortBy: (row) => {
              if (displayItem.attributeProperty.indexOf('.') === -1) {
                const attribute = _.get(row, `node.attributes`, [])
                const returnValue = attribute.find((data) => data.attrId === displayItem.attributeProperty)
                if (returnValue != null) {
                  if (returnValue.attrId === 'orderProductCategory') {
                    return PRODUCT_TYPE[returnValue.attrValue] || ''
                  }
                  return (returnValue || {}).attrValue || ''
                }
                return ''
              }
            },
            isAvailable: true, isActive: displayItem.isDisplay,
            sortable: (displayItem.attributeProperty === 'orderProductCategory' ? 'custom' : false),
            displayOrder: displayItem.displayOrder
          }
        )
      }

      returnValue.push(
        { // アクションボタンを最後尾に配置するために最後に追加する
          itemKey: 'operation', isAvailable: true, isActive: true,
          type: 'Slot', label: '', path: 'operation', width: '60px', columnKey: 'operation', class: 'right',
          isShow: () =>
            // いずれかの表示権限がある場合は表示を行う。
            this.$isFunctionShown(define.PermissionKeys.Order.List.PENDING) ||
            this.$isFunctionShown(define.PermissionKeys.Order.List.SITE) ||
            this.$isFunctionShown(define.PermissionKeys.Order.List.ORDER_NUMBER) ||
            this.$isFunctionShown(define.PermissionKeys.Order.List.ORDER_TYPE) ||
            this.$isFunctionShown(define.PermissionKeys.Order.List.ORDER_DATETIME) ||
            this.$isFunctionShown(define.PermissionKeys.Order.List.PAYMENT_METHOD) ||
            this.$isFunctionShown(define.PermissionKeys.Order.List.PURCHASE_AMOUNT) ||
            this.$isFunctionShown(define.PermissionKeys.Order.List.PRODUCT_AMOUNT) ||
            this.$isFunctionShown(define.PermissionKeys.Order.List.SHIPPING_AMOUNT) ||
            this.$isFunctionShown(define.PermissionKeys.Order.List.CUSTOMER_NO) ||
            this.$isFunctionShown(define.PermissionKeys.Order.List.ORDERER_NAME) ||
            this.$isFunctionShown(define.PermissionKeys.Order.List.ORDERER_MAIL) ||
            this.$isFunctionShown(define.PermissionKeys.Order.List.ORDERER_NAME_KANA) ||
            this.$isFunctionShown(define.PermissionKeys.Order.List.ORDERER_POSTAL_CODE) ||
            this.$isFunctionShown(define.PermissionKeys.Order.List.ORDERER_STATE) ||
            this.$isFunctionShown(define.PermissionKeys.Order.List.ORDERER_CITY) ||
            this.$isFunctionShown(define.PermissionKeys.Order.List.ORDERER_ADDRESS_1) ||
            this.$isFunctionShown(define.PermissionKeys.Order.List.ORDERER_ADDRESS_2) ||
            this.$isFunctionShown(define.PermissionKeys.Order.List.ORDERER_SUITE) ||
            this.$isFunctionShown(define.PermissionKeys.Order.List.ORDERER_PHONE) ||
            this.$isFunctionShown(define.PermissionKeys.Order.List.SHIPMENT_NAME) ||
            this.$isFunctionShown(define.PermissionKeys.Order.List.SHIPMENT_NAME_KANA) ||
            this.$isFunctionShown(define.PermissionKeys.Order.List.SHIPMENT_POSTAL_CODE) ||
            this.$isFunctionShown(define.PermissionKeys.Order.List.SHIPMENT_STATE) ||
            this.$isFunctionShown(define.PermissionKeys.Order.List.SHIPMENT_CITY) ||
            this.$isFunctionShown(define.PermissionKeys.Order.List.SHIPMENT_ADDRESS_1) ||
            this.$isFunctionShown(define.PermissionKeys.Order.List.SHIPMENT_ADDRESS_2) ||
            this.$isFunctionShown(define.PermissionKeys.Order.List.SHIPMENT_SUITE) ||
            this.$isFunctionShown(define.PermissionKeys.Order.List.SHIPMENT_PHONE) ||
            this.$isFunctionShown(define.PermissionKeys.Order.List.SHIPMENT_MAIL) ||
            this.$isFunctionShown(define.PermissionKeys.Order.List.TRACKING_NO) ||
            this.$isFunctionShown(define.PermissionKeys.Order.List.SLIP_DATETIME) ||
            this.$isFunctionShown(define.PermissionKeys.Order.List.DELIVERY_DATETIME) ||
            this.$isFunctionShown(define.PermissionKeys.Order.List.EXPORTED_DATETIME) ||
            this.$isFunctionShown(define.PermissionKeys.Order.List.ORDER_STATUS) ||
            this.$isFunctionShown(define.PermissionKeys.Order.List.FEE_AMOUNT) ||
            this.$isFunctionShown(define.PermissionKeys.Order.List.OTHER_AMOUNT) ||
            this.$isFunctionShown(define.PermissionKeys.Order.List.YELL_AMOUNT)
        }
      )

      for (const displayItem of (this.displayItems || []).filter((data) => !data.isAttribute)) {
        const item = returnValue.find((data) => data.itemKey === displayItem.itemKey)
        if (item) {
          _.set(item, 'isAvailable', true)
          _.set(item, 'isActive', displayItem.isDisplay)
          _.set(item, 'displayOrder', displayItem.displayOrder)
        }
      }

      return returnValue.sort((a, b) => a.displayOrder < b.displayOrder ? -1 : 1)
    },
    showTooltip () {
      return this.mode === ACTION_MODE.NORMAL
    },

    /** メール送信アクション */
    disableMailButton () {
      // 実行権限なし、又は一覧の件数が 0 以下の場合は非活性
      return this.$isFunctionDisabled(define.PermissionKeys.Order.List.MAIL) || this.sortedList.length <= 0
    },
    showProductUnit () {
      return this._tableSetList.some((item) => item.isProductUnit && item.isActive && item.isAvailable)
    },
    isMaxGetCount () {
      return this.$isFunctionShown(define.PermissionKeys.Order.List.MAX_GET_COUNT)
    }
  },
  watch: {
    displayItems: {
      handler: function (val) {
        this.$store.dispatch({
          type: 'set',
          states: {
            'Order/tableSetList': this._tableSetList
          }
        })
      },
      deep: true
    }
  },
  async created () {
    this.$loadingService.show()
    try {
      // テンプレート情報取得
      await this.getMailTemplateData()

      // 設定tableSetListの情報取得
      const work = await common.getDisplayItems('order_list')

      this.$store.dispatch({
        type: 'set',
        states: {
          'Order/displayItems': work
        }
      })

      this.$store.dispatch({
        type: 'set',
        states: {
          'Order/tableSetList': this._tableSetList
        }
      })

    } catch (error) {
      this.$notify.error({ error: error })
      throw error
    } finally {
      this.$loadingService.close()
    }
  },
  onBeforeEnter (event) {
    this.$_tableRowAnimator_cancelAnimation = true
  },
  onBeforeUnload (event) {
    this.$_tableRowAnimator_cancelAnimation = false
  },
  methods: {
    /**
     * アクションボタン
     * @param {Number} mode モード
     */
    actionCommand: async function (mode) {
      switch (mode) {
        case ACTION_MODE.MAIL:
          this.mailTemplateDialogVisible = true
          break
        case ACTION_MODE.SEARCH:
          // 検索条件設定ダイアログ表示
          this.isSearchConditionListDialog = true
          break
        case ACTION_MODE.DISPLAY:
          // 表示項目設定ダイアログ表示
          this.isDisplayListDialog = true
          break
      }
    },
    /**
     * 注文ステータスを返却します。
     * @param {Object} row 注文情報
     * @returns {Array} tag 生成のための配列
     */
    orderStatus: function (row) {
      return this.$_orderUtil_headerOrderStatusTag(row.node)
    },
    /**
     * 支払ステータスを返却します。
     * @param {Object} row 注文情報
     * @returns {String} paymentStatus 支払ステータスのデコード値
     */
    paymentStatus: function (row) {
      if (row == null || row === undefined || !row.node.payments) {
        return ''
      }
      let paymentStatus = this.$_orderUtil_orderPayment_isPaid(row.node.payments.at(-1), row.node.isPaid, row.node.orderTotalPrice.gross)
      return this.$_mixin_getCodeName(paymentStatus, 'd_order.is_paid', { siteCode: this.siteCode })
    },
    /**
     * 指定された最新注文IDの注文記録情報を取得します
     * @param {Object} row 注文情報
     * @returns {String} tag
     */
    orderNotes: function (row) {
      let orderPaymentTag = []
      if (row.node.notes.length > 0) {
        orderPaymentTag.push({ label: 'あり', class: 'tag-green-light-1', type: 'info' })
      } else {
        orderPaymentTag.push({ label: 'なし', type: 'info' })
      }
      return orderPaymentTag
    },
    /**
     * 支払方法を返却します。
     * @param {Object} 注文情報
     * @returns {Array} tag 生成のための配列
     */
    paymentMethod: function (row) {
      return this.$_orderUtil_orderPaymentTag(row.node.payments)
    },
    /**
     * 購入金額を返却します。
     * @param {Object} 注文情報
     * @returns {Array} tag 生成のための配列
     */
    grossPrice: function (row) {
      return common.concatCurrency(row.node.orderTotalPrice.gross, row.node.currency)
    },
    /**
     * Tooltipの項目を返却する.
     * @param {Object} row 注文情報
     * @return {Array}
     */
    getTooltipList (row) {
      return [
        {
          title: '注文修正',
          disabled: () => this.disableEditOrderButton(row) ||
              this.$isFunctionDisabled(define.PermissionKeys.Order.Detail.EDIT),
          click: (item) => this.operationButton(row, define.ORDER_MODE.EDIT),
          show: this.$isFunctionShown(define.PermissionKeys.Order.Detail.EDIT)
        }, {
          title: '再送',
          disabled: () => this.disableShortageButton(row) ||
              this.$isFunctionDisabled(define.PermissionKeys.Order.Detail.SHORTAGE),
          click: (item) => this.operationButton(row, define.ORDER_MODE.SHORTAGE),
          show: this.$isFunctionShown(define.PermissionKeys.Order.Detail.SHORTAGE)
        }, {
          title: '交換出荷',
          disabled: () => this.disableExchangeButton(row) ||
              this.$isFunctionDisabled(define.PermissionKeys.Order.Detail.EXCHANGE),
          click: (item) => this.operationButton(row, define.ORDER_MODE.EXCHANGE),
          show: this.$isFunctionShown(define.PermissionKeys.Order.Detail.EXCHANGE)
        }, {
          title: '返品登録',
          disabled: () => this.disableReceivedRegisterButton(row) ||
              this.$isFunctionDisabled(define.PermissionKeys.Order.Detail.RECEIVED),
          click: (item) => this.operationButton(row, define.ORDER_MODE.RECEIVED),
          show: this.$isFunctionShown(define.PermissionKeys.Order.Detail.RECEIVED)
        }, {
          title: '手動決済',
          disabled: () => this.disableCaptureButton(row) ||
              this.$isFunctionDisabled(define.PermissionKeys.Order.Detail.CAPTURE),
          click: (item) => this.operationButton(row, define.ORDER_MODE.CAPTURE),
          show: this.$isFunctionShown(define.PermissionKeys.Order.Detail.CAPTURE)
        }, {
          title: '一括取消',
          disabled: () => this.disableOrderCancelButton(row) ||
              this.$isFunctionDisabled(define.PermissionKeys.Order.Detail.CANCEL),
          click: (item) => this.operationButton(row, define.ORDER_MODE.CANCEL),
          show: this.$isFunctionShown(define.PermissionKeys.Order.Detail.CANCEL)
        }, {
          title: 'O-Plux',
          click: (item) => this.onOpluxClick(row),
          show: this.$isFunctionShown(define.PermissionKeys.Order.Detail.OPLUX) &&
            this.isEableOplux(row)
        }, {
          title: '一括出荷取消',
          disabled: () => this.disableOrderCancelShipmentButton(row) ||
              this.$isFunctionDisabled(define.PermissionKeys.Order.Detail.CANCEL_SHIPMENT),
          click: (item) => this.onCancelShipmentClick(row),
          show: this.$isFunctionShown(define.PermissionKeys.Order.Detail.CANCEL_SHIPMENT)
        }
      ]
    },
    /**
     * 注文修正ボタン
     * @param {Object} row 注文情報
     * @return {Boolean}
     */
    disableEditOrderButton: function (row) {
      return !this.$_orderUtil_isEditableByQty(this.getItems(row))
    },
    /**
     * 再送ボタン
     * @param {Object} row 注文情報
     * @return {Boolean}
     */
    disableShortageButton: function (row) {
      return !this.$_orderUtil_isShortageShippableByQty(this.getItems(row))
    },
    /**
     * 交換ボタン
     * @param {Object} row 注文情報
     * @return {Boolean}
     */
    disableExchangeButton: function (row) {
      return !this.$_orderUtil_isExchangeableByQty(this.getItems(row))
    },
    /**
     * 返品ボタン
     * @param {Object} row 注文情報
     * @return {Boolean}
     */
    disableReceivedRegisterButton: function (row) {
      return !this.$_orderUtil_isReceivedFixableByQty(this.getItems(row))
    },
    /**
     * 手動決済ボタン
     * @param {Object} row 注文情報
     * @return {Boolean}
     */
    disableCaptureButton: function (row) {
      return !this.$_orderUtil_isCapturable(row.node.payments, row.node.paymentSets)
    },
    /**
     * 一括取消ボタン
     * @param {Object} row 注文情報
     * @return {Boolean}
     */
    disableOrderCancelButton: function (row) {
      return !this.$_orderUtil_isCancellableByQty(this.getItems(row))
    },
    /**
     * O-PLUXボタン
     * @param {Object} row 注文情報
     * @return {Boolean} イベントIDが存在する場合は true
     */
    isEableOplux: function (row) {
      const redEventId = _.keyBy((row.node || []).attributes, 'attrId').redEventId
      return redEventId || false
    },
    /**
     * 一括出荷取消ボタン
     * @param {Object} row 注文情報
     * @return {Boolean}
     */
    disableOrderCancelShipmentButton: function (row) {
      return !this.$_orderUtil_isShipmentCancellableByQty(this.getItems(row))
    },
    /**
     * オペレーションボタンクリック
     * リンククリックコール、store の更新、注文詳細への遷移を行います。
     * @param {Object} row 注文情報
     * @param {Number} mode 注文詳細画面モード
     */
    operationButton: function (row, mode) {
      // 修正NGの状態がないかチェック
      if (this.$_orderUtil_showOrderExportInProcessDialogIfNeed(row.node) ||
          this.$_orderUtil_showUneditableOrderPaymentDialogIfNeed(row.node)) {
        return
      }

      this.$_mixin_onLinkClick(this.findIndex(row), this.storePath, mode)
      this.$store.dispatch({
        type: 'set',
        states: {
          'Order/returnList': true
        }
      })
      this.$router.push({ path: 'order/' + this.$_mixin_getData(row, this.linkKey, '&') })
    },
    /**
     * O-Pluxボタンクリック
     */
    onOpluxClick (row) {
      // 行データをセット
      this.opluxData = _.pick(row, ['node.uuid', 'node.lastUpdatedAt', 'node.redResult', 'node.attributes'])
      // モーダル表示
      this.isOpluxDialog = true
    },    /**
     * 一括出荷取消ボタンクリック
     * @param {Object} row 注文情報
     */
    onCancelShipmentClick (row) {
      const orderNo = _.get(row, 'node.orderNo', [])
      const siteCode = _.get(row, 'node.site.code', [])
      this.$confirm('対象の出荷を取り消します。よろしいですか？', define.MESSAGE_TITLE,
        {
          confirmButtonText: 'はい',
          cancelButtonText: 'いいえ',
          type: 'warning'
        }).then(async () => {

        try {
          if (!this.$validator.validate()) {
            return
          }
          // 注文番号取得
          const csvResources = [{ 'order_no': orderNo }]
          // csvファイル作成
          const csvFile = this.createCsv(csvResources)

          this.$loadingService.show()

          // ハッシュ + ファイル名
          let uploadName = orderNo + '.csv'

          const time = moment().tz('Asia/Tokyo').format('YYYYMMDD_HHmmss')

          const url = new URL(config.GET_S3_SIGNED_URL)
          url.searchParams.set('urlType', 1)
          url.searchParams.set('key1', 'upload')
          url.searchParams.set('key2', 'upload_folder_key')
          url.searchParams.set('key3', 'temp')
          url.searchParams.set('fileKey', time + '_' + uploadName)

          // 署名付きURL取得API呼び出し
          const response = await common.apiAxios(url,
            { method: 'get' })
          const signedURL = response.data.signedUrl

          // アップロード(署名付きURLにPUT)
          const request = new XMLHttpRequest()
          request.open('PUT', signedURL, false)
          request.send(csvFile)
          if (request.status !== 200) {
            throw new Error('アップロードリクエストに失敗しました。')
          }

          // アップロードファイルパス(署名なし)
          let fileKey = new URL(signedURL)
          // pathname: /{HOST}.s3.cc-s.net/backup/import/upload/ time + uploadName
          // 「バックアップフォルダ/ time + uploadName」に加工
          fileKey = fileKey.pathname.split('/').slice(2).join('/')

          // 出荷取消テンプレートid取得
          const cancelShipmentTemplateId = _.get(this.getServiceSiteConfig(siteCode, 'm_upload_template', 'cancel_shipment_template', 'refid'), 'v.0')
          if (cancelShipmentTemplateId === undefined) {
            throw new Error('出荷取消テンプレートIDが不正です。')
          }

          // アップロードAPI呼び出し
          const method = 'post'
          await common.apiAxios(config.UPLOAD, {
            method,
            data: { uploadTemplateId: cancelShipmentTemplateId, fileKey: fileKey }
          })

          // 親の再描画イベントを呼ぶ
          this.$emit('getLatest')
        } catch (error) {
          this.$notify.error({ error: error })
          throw error
        } finally {
          this.$loadingService.close()
        }
        return false
      }).catch(() => {})
    },
    /**
      * CSVファイルを作成する
      * @param {*} csvResources csvデータ
      */
    createCsv (csvResources) {
      const csv = _.chain(csvResources).map(val => {
        return [
          val.order_no
        ].join(',')
      }).join('\n').value()
      return csv
    },
    onRowClick (row, column, event) {
      if ((column || {}).columnKey === 'operation' ||
        this.mode !== ACTION_MODE.NORMAL) {
        // tooltipのセルクリック時は何もしない
        // モードが通常以外は何もしない
        return
      }

      const id = this.$_mixin_getData(row, this.linkKey, '&')
      const index = this.findIndex(row)
      this.$_mixin_onLinkClick(index, this.storePath)
      this.$router.push({ path: `/order/${id}` })
    },
    findIndex (row) {
      let condition = _.chain(this.linkKey)
        .reduce((acc, cur) => {
          acc[cur] = _.get(row, cur)
          return acc
        }, {})
        .value()
      return this.$_mixin_findIndex(this.sortedList, condition)
    },
    getItems (row) {
      return _.chain(row)
        .get('node.shipments')
        .flatMap(val => val.items)
        .value()
    },
    dataConvert (result) {
      // 不要なnode直下のattributeを除去する
      const needAttribute = (this.displayItems || [])
        .filter((data) => data.isAttribute && data.attributeProperty.indexOf('.') === -1)
        .map((data) => data.attributeProperty)

      // 不要なnode.ドット区切りパスのattributeを除去する
      // (ex attributeProperty:shipments.estimatedDeliveryDateText → path:node.shipments, attrId:estimatedDeliveryDateText)
      const dotsAttributes = (this.displayItems || [])
        .filter((data) => data.isAttribute && data.attributeProperty.indexOf('.') !== -1)
        .map((data) => data.attributeProperty)

      const needShipmentsProperty = []
      for (const dotsAttribute of dotsAttributes) {
        const pathArray = dotsAttribute.split('.')
        const attributeProperty = pathArray.pop()
        needShipmentsProperty.push(attributeProperty)
      }

      result.forEach((data) => {
        _.set(data, 'node.attributes',
          _.get(data, 'node.attributes', []).filter((data) => {
            return needAttribute.includes(data.attrId) || data.attrId.startsWith('red')
          }))

        // shipmentsのattributes追加により、shipments直下の不要attributesを削除するよう暫定対応
        // 今後attributes取得箇所が増える場合、全探索に改修予定
        const shipments = _.get(data, 'node.shipments', [])
        for (let i = 0; i < shipments.length; ++i) {
          _.set(data, `node.shipments.${i}.attributes`,
            _.get(data, `node.shipments.${i}.attributes`, []).filter((attribute) => {
              return needShipmentsProperty.includes(attribute.attrId)
            }))
        }
      })
    },
    /**
     * キャンセルボタン
     */
    cancel: async function () {
      switch (this.mode) {
        case ACTION_MODE.MAIL:
          this.showSelection = false
          const tableData = _.get(this.$refs, 'listPage.$refs.tableList.$refs.table', {})
          tableData.clearSelection()
          break
      }
      this.mode = ACTION_MODE.NORMAL
      this.$_ConfirmDiscardOnLeave_watchEnd()
    },
    /**
     * 送信ボタン
     */
    sendMail: async function (mode) {
      // メール送信APIの呼び出し
      this.$confirm('メールを送信しますか？', define.MESSAGE_TITLE, {
        confirmButtonText: 'はい',
        cancelButtonText: 'いいえ',
        type: 'warning'
      }).then(async () => {
        try {
          this.$loadingService.show()
          // メール送信API呼び出し
          const url = config.MAIL_NOTIFY
          const method = 'post'
          await common.apiAxios(url, {
            method,
            data: {
              templateUuid: this.selectedMailTemplate,
              records:
                _.get(this.$refs, 'listPage.$refs.tableList.$refs.table.selection', []).map((row) => {
                  return {
                    order: {
                      uuid: row.node.uuid
                    }
                  }
                })
            }
          })

          this.$notify.success('送信処理を行いました。')
          this.$_ConfirmDiscardOnLeave_watchEnd()
          this.showSelection = false
          this.mode = ACTION_MODE.NORMAL
          const tableData = _.get(this.$refs, 'listPage.$refs.tableList.$refs.table', {})
          tableData.clearSelection()
        } catch (error) {
          common.addError(error, { msg: message.MSG_539() })
          this.$notify.error({ error: error })
          throw error
        } finally {
          this.$loadingService.close()
        }
      }).catch(() => {})
    },
    mailTemplateDialogCancelClose: async function () {
      this.mailTemplateDialogVisible = false
    },
    mailTemplateDialogOK: async function () {
      if (!await this.$validator.validate()) {
        return
      }

      this.$_ConfirmDiscardOnLeave_watchStart(this.mode, 'メール送信を中止しますか？')
      this.showSelection = true
      this.mode = ACTION_MODE.MAIL
      this.mailTemplateDialogVisible = false
    },
    async getMailTemplateData () {
      let param = common.addGraphQlCondition(mailQuery.GRAPH_QL_QUERY_MAIL_TEMPLATE_SIMPLE,
        { types: 99 })
      const res = await common.postGraphQl(param)
      this.mailTemplate = (common.getValueByPath(res.data, 'data.mailTemplates.edges') || [])
        .map((template) => { return { uuid: template.node.uuid, templateName: template.node.templateName } })
      this.selectedMailTemplate = (this.mailTemplate[0] || {}).uuid || ''
    },
    clickDisplaySave () {
      this.isDisplaySaveDialog = true
    },
    clickDisplaySelect () {
      this.isDisplaySelectDialog = true
    },
    // 属性情報(attribute)のある目的階層になるまで再帰
    // 目的階層に到達したら、属性情報単位の表示用htmlを返却
    digAttributeValueForHtml (nodes, pathArray, index, attributeProperty) {
      if (index === pathArray.length) {
        return this.getHtmlUnit(nodes, attributeProperty)
      } else {
        index += 1
        let returnHtml = ''
        for (const node of nodes[pathArray[index - 1]]) {
          returnHtml += this.digAttributeValueForHtml(node, pathArray, index, attributeProperty)
        }
        return returnHtml
      }
    },
    // 表に出力する対象パスの表示用htmlの返却(属性情報単位)
    getHtmlUnit (attribute, attributeProperty) {
      const value = _.get(attribute, 'attributes', '')
      const returnValue = value.find(data => data.attrId === attributeProperty)
      const rowSpan = this.showProductUnit ? (attribute.items || ['']).length : 1
      const htmlStr = `
        <tr>
          <td>
            <div style='height:${rowSpan * 53 - 25}px;width:100%;text-overflow: ellipsis;overflow-x: hidden; white-space: nowrap;'}>${(returnValue || {}).attrValue || ''}</div>
          </td>
        </tr>
      `
      return htmlStr
    },
    getGrossPrice (row, type) {
      const shipments = _.get(row, 'node.shipments', [])
      let totalPrice = 0
      for (const shipment of shipments) {
        let rows = null
        if (type === 'product') {
          rows = shipment.items
        } else {
          rows = shipment.shippingItems
        }
        for (const rowData of rows) {
          if (type === 'product') {
            totalPrice += Number(rowData.price.gross)
          } else if (type === 'shipping') {
            if (rowData.productId === 'STANDARD_SHIPPING') {
              totalPrice += Number(rowData.price.gross)
            }
          } else if (type === 'fee') {
            if (rowData.productId === 'COD_Fee' || rowData.productId === 'CVS_Fee') {
              totalPrice += Number(rowData.price.gross)
            }
          }
        }
        return common.concatCurrency(totalPrice, row.node.currency)
      }
    },
    getGrossPriceValue (row, type) {
      const shipments = _.get(row, 'node.shipments', [])
      let totalPrice = 0
      for (const shipment of shipments) {
        let rows = null
        if (type === 'product') {
          rows = shipment.items
        } else {
          rows = shipment.shippingItems
        }
        for (const rowData of rows) {
          if (type === 'product') {
            totalPrice += Number(rowData.price.gross)
          } else if (type === 'shipping') {
            if (rowData.productId === 'STANDARD_SHIPPING') {
              totalPrice += Number(rowData.price.gross)
            }
          } else if (type === 'fee') {
            if (rowData.productId === 'COD_Fee' || rowData.productId === 'CVS_Fee') {
              totalPrice += Number(rowData.price.gross)
            }
          }
        }
        return totalPrice
      }
    },
    getAdjustGrossPrice (row) {
      const shipments = _.get(row, 'node.shipments', [])
      let adjustmentPrice = 0
      for (const shipment of shipments) {
        for (const item of shipment.items) {
          let adjustments = _.get(item, 'priceAdjustments', [])
          for (const adjustment of adjustments) {
            if (adjustment.adjustType === '2' && adjustment.promotionId === 'yell') {
              if (adjustment.price.gross[0] === '-') {
                adjustmentPrice -= Number.parseFloat(adjustment.price.gross.slice(1))
              } else {
                adjustmentPrice += Number.parseFloat(adjustment.price.gross)
              }
            }
          }
        }
      }
      return common.concatCurrency(adjustmentPrice, row.node.currency)
    },
    getAdjustGrossPriceValue (row) {
      const shipments = _.get(row, 'node.shipments', [])
      let adjustmentPrice = 0
      for (const shipment of shipments) {
        for (const item of shipment.items) {
          let adjustments = _.get(item, 'priceAdjustments', [])
          for (const adjustment of adjustments) {
            if (adjustment.adjustType === '2' && adjustment.promotionId === 'yell') {
              if (adjustment.price.gross[0] === '-') {
                adjustmentPrice -= Number.parseFloat(adjustment.price.gross.slice(1))
              } else {
                adjustmentPrice += Number.parseFloat(adjustment.price.gross)
              }
            }
          }
        }
      }
      return adjustmentPrice
    },
    getMerchandiseAdjustGrossPrice (row) {
      const adjustments = _.get(row, 'node.merchandisePriceAdjustments', [])
      let adjustmentPrice = 0
      for (const adjustment of adjustments) {
        if (adjustment.adjustType === '1') {
          if (adjustment.price.gross[0] === '-') {
            adjustmentPrice -= Number.parseFloat(adjustment.price.gross.slice(1))
          } else {
            adjustmentPrice += Number.parseFloat(adjustment.price.gross)
          }
        }
      }
      return common.concatCurrency(adjustmentPrice, row.node.currency)
    },
    getMerchandiseAdjustGrossPriceValue (row) {
      const adjustments = _.get(row, 'node.merchandisePriceAdjustments', [])
      let adjustmentPrice = 0
      for (const adjustment of adjustments) {
        if (adjustment.adjustType === '1') {
          if (adjustment.price.gross[0] === '-') {
            adjustmentPrice -= Number.parseFloat(adjustment.price.gross.slice(1))
          } else {
            adjustmentPrice += Number.parseFloat(adjustment.price.gross)
          }
        }
      }
      return adjustmentPrice
    },
    // ソートを行うために複数件あるデータの最小値を返す
    getSortableData (row, path, targetList, colsep = '', type = 'String') {
      let returnValue = null
      const records = _.get(row, path, [])
      for (const record of records) {
        let targetStr = ''
        for (const targetData of targetList) {
          let value = _.get(record, targetData, '')
          if (value !== '' && value !== null) {
            switch (type) {
              case 'Number':
                value = Number(value)
                break
              case 'Datetime':
                value = moment(value).tz('Asia/Tokyo').format('YYYY/MM/DD HH:mm')
                break
              default:
                break
            }
            targetStr += (targetStr === '' ? value : colsep + value)
          }
        }
        if (returnValue == null || returnValue > targetStr) {
          returnValue = targetStr
        }
      }

      // ソート順が不定になるため、データなしの場合、最小値を返す
      if (returnValue === '' || returnValue === null) {
        switch (type) {
          case 'Number':
            returnValue = -1
            break
          case 'Datetime':
            returnValue = '1900/01/01 00:00'
            break
          default:
            returnValue = ''
            break
        }
      }

      return returnValue
    },
    // 複数分割する必要のあるデータ用のHTML文を作成
    getDisplayData (row, path, targetList, colsep = '', type = 'String') {
      let htmlStr = ` <table class='innerTable'> `
      const records = _.get(row, path, [])
      for (const record of records) {
        let targetStr = ''
        for (const targetData of targetList) {
          let value = _.get(record, targetData, '')
          if (value !== '' && value !== null) {
            switch (type) {
              case 'Number':
                value = Number(value)
                break
              case 'Datetime':
                value = moment(value).tz('Asia/Tokyo').format('YYYY/MM/DD HH:mm')
                break
              default:
                break
            }
            targetStr += (targetStr === '' ? value : colsep + value)
          }
        }
        const rowSpan = this.showProductUnit ? (record || ['']).length : 1
        const classStr = (type === 'Number' ? ` class='right'` : '')
        htmlStr += `
            <tr>
              <td>
                <div style='height:${rowSpan * 53 - 25}px;width:100%;text-overflow: ellipsis;overflow-x: hidden; white-space: nowrap;'} ${classStr}>${targetStr}</div>
              </td>
            </tr>
        `
      }
      htmlStr += ' </table> '
      return htmlStr
    },
    // ソートを行うために複数件あるデータの最小値を返す(価格)
    getSortablePrice (row, path, targetType) {
      let returnValue = 0
      const targetValues = []
      const records = _.get(row, path, [])
      for (const record of records) {
        let rows = (targetType === 'product' ? record.items : record.shippingItems)
        let targetValue = 0
        for (const item of rows) {
          if (targetType === 'product') {
            targetValue += Number(item.adjustedPrice.gross)
          } else if (targetType === 'shipping') {
            if (item.productId === 'STANDARD_SHIPPING') {
              targetValue += Number(item.adjustedPrice.gross)
            }
          }
        }
        targetValues.push(targetValue)
      }

      if (targetValues.length !== 0) {
        returnValue = Math.min(...targetValues)
      }
      return Number(returnValue)
    },
    // 複数分割する必要のあるデータ用のHTML文を作成
    getDisplayPrice (row, path, targetType) {
      let htmlStr = ` <table class='innerTable'> `
      const records = _.get(row, path, [])
      for (const record of records) {
        let targetValue = 0
        let rows = (targetType === 'product' ? record.items : record.shippingItems)
        for (const item of rows) {
          if (targetType === 'product') {
            targetValue += Number(item.adjustedPrice.gross)
          } else if (targetType === 'shipping') {
            if (item.productId === 'STANDARD_SHIPPING') {
              targetValue += Number(item.adjustedPrice.gross)
            }
          }
        }
        const rowSpan = this.showProductUnit ? (record || ['']).length : 1
        targetValue = common.concatCurrency(targetValue, row.node.currency)
        htmlStr += `
          <tr>
            <td>
              <div style='height:${rowSpan * 53 - 25}px;width:100%;text-overflow: ellipsis;overflow-x: hidden; white-space: nowrap;'} class='right'>${targetValue}</div>
            </td>
          </tr>
        `
      }

      htmlStr += ' </table> '

      return htmlStr
    },
    // 表に出力する対象パスの表示用htmlの返却（商品単位のもの）
    getHtmlProductUnit (row, path, isNumber = false) {
      let htmlStr = ` <table class='innerTable'> `
      const shipments = _.get(row, 'node.shipments', [])
      for (const shipment of shipments) {
        for (const item of shipment.items) {
          let value = _.get(item, path, '')
          if (isNumber) {
            value = Number(value)
          }
          htmlStr += `
              <tr>
                <td>
                  <div style='width:100%;height:28px;text-overflow: ellipsis;overflow-x: hidden; white-space: nowrap;' ${isNumber ? ` class='right'` : ''}>${value}</div>
                </td>
              </tr>
          `
        }
      }
      htmlStr += ' </table> '
      return htmlStr
    },
    cellStyle (r) {
      return this.$_mixin_cellStyle(r, this.customCellStyle('order_list'), 'node.')
    }
  }
}
</script>

<style scoped>
#orderListTable ::v-deep .tag {
    display: unset;
}

#orderListTable ::v-deep .tag .el-tag {
    margin-left: 3px;
}

.prefixTableRow .el-button {
  text-align: center;
  margin-bottom: 10px;
}
.prefixTableRow .el-button ::v-deep span {
  margin: 0px;
}

.mailTemplateDialog .mailTemplateContents {
  padding: 10px;
  display: flex;
  color: #9da6ba;
  font-weight: bold;
}

.mailTemplateDialog .mailTemplateContents span {
  width: 150px;
  display: flex;
  align-items: center;
}

.mailTemplateDialog .mailTemplateContents .el-select {
  width: 100%;
}

#orderListTable ::v-deep .el-table__header .qty .cell {
  white-space: pre-line!important;
}

#orderListTable ::v-deep .el-table__row .itemHtml {
  padding: 0!important;

}
#orderListTable ::v-deep .el-table__row .itemHtml > .cell {
  padding: 0!important;
}
#orderListTable ::v-deep table {
  border-collapse:collapse
}

#orderListTable ::v-deep tr {
  border-bottom: solid 1px #dddddd;
}

#orderListTable ::v-deep .innerItem:not(th) {
  padding: 0px!important;
  border-left: solid 1px #dddddd;
  border-right: solid 1px #dddddd;
}
#orderListTable ::v-deep .innerItem .cell {
  padding: 0px!important;
}

#orderListTable ::v-deep .el-table__row .innerTable {
  width: 100%;
  table-layout: fixed;
}

#orderListTable ::v-deep .el-table__row .innerTable tr {
  background-color: initial;
}

#orderListTable ::v-deep .el-table__row .innerTable tr:last-child {
  border-bottom: none;
}

#orderListTable ::v-deep .el-table__row .innerTable td {
  padding-left: 10px;
  padding-right: 10px;
}

#orderListTable ::v-deep .el-table__row .innerTable .tag .el-tag {
  min-width: 48%;
  text-align: center;
}

/* 注文一覧の縦幅を固定 */
#orderListTable ::v-deep .el-table__body-wrapper {
  height: 550px;
  overflow: auto;
}

/* アクションボタンを固定 */
/* ヘッダーの最後尾にはdisplay-noneの要素があるため最後から2番目の要素を指定 */
#orderListTable ::v-deep .el-table__header th:nth-last-child(2)  {
  position: sticky !important;
  right: 0 !important;
}
#orderListTable ::v-deep .el-table__body td:last-child  {
  position: sticky !important;
  right: 0 !important;
  background-color: inherit;
}
/* マウスオーバー時の背景色を改めて指定 */
#orderListTable ::v-deep .el-table__body tr:hover>td  {
    background-color: #fcfbed !important;
}

</style>